import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { Loader2, Mail, Lock, User, Plane, Building2, Compass, Car as CarIcon, Check } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1639244132045-94c307ff4bfc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHw0fHxjYXJpYmJlYW4lMjBsdXh1cnklMjBkaW5pbmclMjBiZWFjaCUyMGV4cGVyaWVuY2V8ZW58MHx8fHwxNzg5MjcwNzExfDA&ixlib=rb-4.1.0&q=85";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "traveler", provider_types: ["tour_operator"], agent_handle: "" });
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleType = (t) => setForm((f) => {
    const has = f.provider_types.includes(t);
    let next = has ? f.provider_types.filter((x) => x !== t) : [...f.provider_types, t];
    if (next.length === 0) next = [t]; // keep at least one
    return { ...f, provider_types: next };
  });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = form.role === "provider"
        ? { name: form.name, email: form.email, password: form.password, role: "provider", provider_types: form.provider_types }
        : form.role === "agent"
        ? { name: form.name, email: form.email, password: form.password, role: "agent", agent_handle: form.agent_handle }
        : { name: form.name, email: form.email, password: form.password, role: "traveler" };
      const u = await register(payload);
      toast.success("Account created — welcome aboard!");
      const ret = searchParams.get("return");
      navigate(ret || (u.role === "provider" ? "/provider" : u.role === "agent" ? "/agent" : "/dashboard"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] grid lg:grid-cols-2">
      <div className="flex items-center justify-center px-6 py-16 order-2 lg:order-1">
        <div className="w-full max-w-md fade-up">
          <div className="mb-8">
            <Logo size={40} tagline />
          </div>
          <h1 className="font-heading text-3xl font-bold text-white">Create your account</h1>
          <p className="text-slate-400 mt-2 mb-6 text-sm">Join the premium Caribbean marketplace.</p>

          {/* Role toggle */}
          <div className="grid grid-cols-3 gap-2 mb-6">
            {[
              { key: "traveler", label: "Traveler", desc: "Book tours", icon: Plane },
              { key: "provider", label: "Host", desc: "List & earn", icon: Building2 },
              { key: "agent", label: "Agent", desc: "Promote & earn", icon: Compass },
            ].map((r) => (
              <button
                key={r.key}
                type="button"
                data-testid={`role-${r.key}`}
                onClick={() => set("role", r.key)}
                className={`text-left rounded-xl p-3 border transition-all ${
                  form.role === r.key ? "neon-border bg-[#0B0E1B]" : "border-[#1D2640] glass hover:border-cyan-400/30"
                }`}
              >
                <r.icon className={`w-5 h-5 mb-2 ${form.role === r.key ? "text-cyan-400" : "text-slate-400"}`} />
                <p className="font-heading font-semibold text-white text-sm">{r.label}</p>
                <p className="text-[11px] text-slate-400">{r.desc}</p>
              </button>
            ))}
          </div>

          {/* Agent handle */}
          {form.role === "agent" && (
            <div className="mb-6" data-testid="agent-handle-field">
              <label className="text-sm text-slate-300 mb-1.5 block">Your unique agent handle</label>
              <div className="flex items-center gap-1 bg-[#0B0E1B] border border-[#1D2640] rounded-xl px-4 py-3 focus-within:border-cyan-400/50">
                <span className="text-slate-500 text-sm">carivisor.gd/…?agent=</span>
                <input data-testid="register-agent-handle" value={form.agent_handle}
                  onChange={(e) => set("agent_handle", e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ""))}
                  placeholder="sarah" className="bg-transparent text-cyan-300 font-semibold text-sm w-full outline-none" />
              </div>
              <p className="text-[11px] text-cyan-300/80 mt-2">Agents earn a commission on every booking made through their tracking links. Accounts require admin approval after signup.</p>
            </div>
          )}

          {/* Provider type selection */}
          {form.role === "provider" && (
            <div className="mb-6" data-testid="provider-type-select">
              <p className="text-xs text-slate-400 mb-2">What do you offer? (choose one or both)</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: "tour_operator", label: "Tour Operator", desc: "Tours & excursions", icon: Compass },
                  { key: "car_rental", label: "Car Rental", desc: "Rent out vehicles", icon: CarIcon },
                ].map((t) => {
                  const active = form.provider_types.includes(t.key);
                  return (
                    <button
                      key={t.key}
                      type="button"
                      data-testid={`provider-type-${t.key}`}
                      onClick={() => toggleType(t.key)}
                      className={`text-left rounded-xl p-4 border transition-all relative ${
                        active ? "neon-border bg-[#0B0E1B]" : "border-[#1D2640] glass hover:border-cyan-400/30"
                      }`}
                    >
                      {active && <Check className="w-4 h-4 text-cyan-400 absolute top-3 right-3" />}
                      <t.icon className={`w-5 h-5 mb-2 ${active ? "text-cyan-400" : "text-slate-400"}`} />
                      <p className="font-heading font-semibold text-white text-sm">{t.label}</p>
                      <p className="text-xs text-slate-400">{t.desc}</p>
                    </button>
                  );
                })}
              </div>
              {form.provider_types.includes("car_rental") && (
                <p className="text-[11px] text-cyan-300/80 mt-2">
                  Car rental accounts require document verification (business license, fleet insurance, rental agreement) after signup.
                </p>
              )}
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-sm text-slate-300 mb-1.5 block">Full name</label>
              <div className="flex items-center gap-3 bg-[#0B0E1B] border border-[#1D2640] rounded-xl px-4 py-3 focus-within:border-cyan-400/50">
                <User className="w-4 h-4 text-slate-500" />
                <input data-testid="register-name" required value={form.name} onChange={(e) => set("name", e.target.value)}
                  placeholder="Jane Traveler" className="bg-transparent text-white text-sm w-full outline-none" />
              </div>
            </div>
            <div>
              <label className="text-sm text-slate-300 mb-1.5 block">Email</label>
              <div className="flex items-center gap-3 bg-[#0B0E1B] border border-[#1D2640] rounded-xl px-4 py-3 focus-within:border-cyan-400/50">
                <Mail className="w-4 h-4 text-slate-500" />
                <input data-testid="register-email" type="email" required value={form.email} onChange={(e) => set("email", e.target.value)}
                  placeholder="you@example.com" className="bg-transparent text-white text-sm w-full outline-none" />
              </div>
            </div>
            <div>
              <label className="text-sm text-slate-300 mb-1.5 block">Password</label>
              <div className="flex items-center gap-3 bg-[#0B0E1B] border border-[#1D2640] rounded-xl px-4 py-3 focus-within:border-cyan-400/50">
                <Lock className="w-4 h-4 text-slate-500" />
                <input data-testid="register-password" type="password" required minLength={6} value={form.password} onChange={(e) => set("password", e.target.value)}
                  placeholder="At least 6 characters" className="bg-transparent text-white text-sm w-full outline-none" />
              </div>
            </div>
            <button data-testid="register-submit-btn" disabled={loading}
              className="w-full btn-neon rounded-xl py-3.5 font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account"}
            </button>
          </form>

          <p className="text-sm text-slate-400 text-center mt-6">
            Already have an account? <Link to="/login" className="text-cyan-400 hover:underline">Log in</Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:block relative order-1 lg:order-2">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#060810] via-[#060810]/40 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-right on-dark">
          <h2 className="font-heading text-3xl font-bold text-white">You Create the Experience.<br />We Help Bring the Guests.</h2>
        </div>
      </div>
    </div>
  );
}
